import React, { useState } from 'react';
import { Modal, Button, Checkbox, message } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import { Download, FileArchive, HardDrive, CheckCircle2 } from 'lucide-react';
import axios from 'axios';

/* ── Figma-extracted design tokens ──────────────────────────────── */
const COLORS = {
  teal: '#2a5f5a',
  tealHover: '#245249',
  tealBg5: 'rgba(42,95,90,0.05)',
  white: '#ffffff',
  gray50: '#f9fafb',
  gray100: '#f3f4f6',
  gray200: '#e5e7eb',
  gray300: '#d1d5db',
  gray400: '#9ca3af',
  gray500: '#6b7280',
  gray600: '#4b5563',
  gray700: '#374151',
  gray900: '#111827',
};

const overlayStyle = {
  backgroundColor: 'rgba(0,0,0,0.5)',
};

const modalBodyStyle = {
  padding: 24,
};

const titleRowStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  marginBottom: 4,
};

const titleTextStyle = {
  fontSize: 18,
  fontWeight: 600,
  lineHeight: 1,
  color: COLORS.gray900,
  margin: 0,
};

const descriptionStyle = {
  fontSize: 14,
  color: COLORS.gray600,
  margin: 0,
};

const sectionGap = { marginTop: 24 };

/* Summary card */
const summaryCardStyle = {
  background: COLORS.gray50,
  borderRadius: 8,
  padding: 16,
  border: `1px solid ${COLORS.gray200}`,
};

const summaryGridStyle = {
  display: 'grid',
  gridTemplateColumns: '1fr 1fr 1fr',
  gap: 16,
  textAlign: 'center',
};

const bigNumberStyle = {
  fontSize: 24,
  fontWeight: 700,
  color: COLORS.gray900,
  lineHeight: 1.2,
};

const smallLabelStyle = {
  fontSize: 12,
  color: COLORS.gray600,
  marginTop: 4,
};

/* Studies list */
const listContainerStyle = {
  background: COLORS.gray50,
  border: `1px solid ${COLORS.gray200}`,
  borderRadius: 8,
  padding: 12,
  maxHeight: 192,
  overflowY: 'auto',
};

const listItemStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '8px 12px',
  borderRadius: 4,
  cursor: 'default',
};

const listItemNameStyle = {
  fontSize: 14,
  fontWeight: 500,
  color: COLORS.gray900,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const listItemDetailStyle = {
  fontSize: 12,
  color: COLORS.gray500,
};

/* Format cards */
const formatGridStyle = {
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: 12,
};

const formatCardBase = {
  padding: 16,
  borderRadius: 8,
  borderWidth: 2,
  borderStyle: 'solid',
  transition: 'all 0.2s',
  textAlign: 'left',
  cursor: 'pointer',
  display: 'block',
  width: '100%',
};

const formatCardSelected = {
  ...formatCardBase,
  borderColor: COLORS.teal,
  background: COLORS.tealBg5,
};

const formatCardUnselected = {
  ...formatCardBase,
  borderColor: COLORS.gray200,
  background: COLORS.gray50,
};

const formatContentStyle = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: 12,
};

const formatTitleStyle = {
  fontWeight: 600,
  color: COLORS.gray900,
  marginBottom: 4,
  fontSize: 14,
};

const formatDescStyle = {
  fontSize: 12,
  color: COLORS.gray600,
};

/* Checkbox options */
const checkboxRowStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  padding: 12,
  background: COLORS.gray50,
  borderRadius: 8,
};

/* Progress */
const progressTrackStyle = {
  width: '100%',
  background: COLORS.gray200,
  borderRadius: 9999,
  height: 8,
  overflow: 'hidden',
};

const progressFillBase = {
  background: COLORS.teal,
  height: '100%',
  transition: 'width 300ms',
  borderRadius: 9999,
};

/* Footer */
const footerStyle = {
  display: 'flex',
  justifyContent: 'flex-end',
  gap: 12,
  paddingTop: 16,
  borderTop: `1px solid ${COLORS.gray200}`,
  marginTop: 24,
};

/* Label */
const sectionLabelStyle = {
  fontSize: 14,
  fontWeight: 600,
  color: COLORS.gray900,
  marginBottom: 12,
};

export function BulkDownloadDialog({ studies, open, onOpenChange }) {
  const [downloading, setDownloading] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState(0);
  const [format, setFormat] = useState('zip');
  const [includeReports, setIncludeReports] = useState(true);
  const [includeMetadata, setIncludeMetadata] = useState(true);

  const totalImages = (studies || []).reduce((sum, s) => sum + (parseInt(s.imagesCount, 10) || 0), 0);
  // Rough uncompressed size estimate, per-modality MB/image. The old flat
  // 10 MB/image was ~20x too high for CT/MR (the common case: 1236 CT images
  // showed ~12 GB instead of ~0.6 GB). Cross-sectional modalities are small;
  // projection radiography (DX/CR/DR) and mammography are large. ZIP compresses
  // further, so this is an upper-ish bound.
  const MB_PER_IMAGE = { CT: 0.5, MR: 0.5, PT: 0.5, NM: 0.5, US: 0.2, XA: 8, RF: 8, DX: 12, CR: 12, DR: 12, RG: 12, MG: 40 };
  const estimatedMB = Math.round(
    (studies || []).reduce((sum, s) => {
      const n = parseInt(s.imagesCount, 10) || 0;
      const per = MB_PER_IMAGE[String(s.modality || '').toUpperCase()] || 5;
      return sum + n * per;
    }, 0)
  );
  const estimatedSizeStr = estimatedMB >= 1000
    ? `~${(estimatedMB / 1024).toFixed(1)} GB`
    : `~${estimatedMB} MB`;

  const handleDownload = async () => {
    const studyList = studies || [];
    if (studyList.length === 0) return;

    setDownloading(true);
    setDownloadProgress(0);

    try {
      let completed = 0;
      for (const study of studyList) {
        try {
          const response = await axios.get(`/orthanc/studies/${study.id}/archive`, {
            responseType: 'blob',
          });

          const url = window.URL.createObjectURL(new Blob([response.data]));
          const link = document.createElement('a');
          link.href = url;
          const filename = `${study.patientName || study.patientID || study.id}.zip`;
          link.setAttribute('download', filename);
          document.body.appendChild(link);
          link.click();
          link.remove();
          window.URL.revokeObjectURL(url);
        } catch (err) {
          console.error(`Failed to download study ${study.id}:`, err);
        }

        completed++;
        setDownloadProgress(Math.round((completed / studyList.length) * 100));
      }

      message.success(`${studyList.length} ${studyList.length === 1 ? 'study' : 'studies'} downloaded successfully`);
      setTimeout(() => {
        onOpenChange(false);
        setDownloadProgress(0);
      }, 1000);
    } catch (error) {
      console.error('Bulk download error:', error);
      message.error('Failed to download studies');
    } finally {
      setDownloading(false);
    }
  };

  const handleClose = () => {
    if (!downloading) {
      onOpenChange(false);
      setDownloadProgress(0);
      setFormat('zip');
      setIncludeReports(true);
      setIncludeMetadata(true);
    }
  };

  const studyCount = (studies || []).length;

  return (
    <Modal
      visible={open}
      onCancel={handleClose}
      footer={null}
      width={672}
      centered
      destroyOnClose
      maskStyle={overlayStyle}
      bodyStyle={modalBodyStyle}
      closable={!downloading}
    >
      {/* Header */}
      <div>
        <div style={titleRowStyle}>
          <Download size={20} color={COLORS.teal} />
          <h3 style={titleTextStyle}>Bulk Download Studies</h3>
        </div>
        <p style={descriptionStyle}>
          Download {studyCount} {studyCount === 1 ? 'study' : 'studies'} with {totalImages} total images
        </p>
      </div>

      {/* Studies Summary */}
      <div style={sectionGap}>
        <div style={summaryCardStyle}>
          <div style={summaryGridStyle}>
            <div>
              <div style={bigNumberStyle}>{studyCount}</div>
              <div style={smallLabelStyle}>Studies</div>
            </div>
            <div>
              <div style={bigNumberStyle}>{totalImages}</div>
              <div style={smallLabelStyle}>Images</div>
            </div>
            <div>
              <div style={bigNumberStyle}>{estimatedSizeStr}</div>
              <div style={smallLabelStyle}>Estimated Size</div>
            </div>
          </div>
        </div>
      </div>

      {/* Selected Studies List */}
      <div style={sectionGap}>
        <div style={sectionLabelStyle}>Selected Studies</div>
        <div style={listContainerStyle}>
          {(studies || []).map((study) => (
            <div key={study.id} style={listItemStyle}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={listItemNameStyle}>{study.patientName}</div>
                <div style={listItemDetailStyle}>
                  {study.patientID} &bull; {study.modality} &bull; {study.imagesCount || 0} images
                </div>
              </div>
              <CheckCircle2 size={16} color={COLORS.teal} style={{ flexShrink: 0, marginLeft: 8 }} />
            </div>
          ))}
        </div>
      </div>

      {/* Download Format */}
      <div style={sectionGap}>
        <div style={sectionLabelStyle}>Download Format</div>
        <div style={formatGridStyle}>
          <div
            role="button"
            tabIndex={0}
            onClick={() => setFormat('zip')}
            onKeyDown={(e) => e.key === 'Enter' && setFormat('zip')}
            style={format === 'zip' ? formatCardSelected : formatCardUnselected}
          >
            <div style={formatContentStyle}>
              <FileArchive size={20} color={format === 'zip' ? COLORS.teal : COLORS.gray400} style={{ marginTop: 2 }} />
              <div>
                <div style={formatTitleStyle}>ZIP Archive</div>
                <div style={formatDescStyle}>Compressed archive with all files</div>
              </div>
            </div>
          </div>

          <div
            role="button"
            tabIndex={0}
            onClick={() => setFormat('dicom')}
            onKeyDown={(e) => e.key === 'Enter' && setFormat('dicom')}
            style={format === 'dicom' ? formatCardSelected : formatCardUnselected}
          >
            <div style={formatContentStyle}>
              <HardDrive size={20} color={format === 'dicom' ? COLORS.teal : COLORS.gray400} style={{ marginTop: 2 }} />
              <div>
                <div style={formatTitleStyle}>DICOM Files</div>
                <div style={formatDescStyle}>Raw DICOM format (uncompressed)</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Include Additional Files */}
      <div style={sectionGap}>
        <div style={sectionLabelStyle}>Include Additional Files</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={checkboxRowStyle}>
            <Checkbox
              checked={includeReports}
              onChange={(e) => setIncludeReports(e.target.checked)}
            />
            <span style={{ fontSize: 14, color: COLORS.gray900, cursor: 'pointer' }}>
              Include radiology reports (PDF)
            </span>
          </div>
          <div style={checkboxRowStyle}>
            <Checkbox
              checked={includeMetadata}
              onChange={(e) => setIncludeMetadata(e.target.checked)}
            />
            <span style={{ fontSize: 14, color: COLORS.gray900, cursor: 'pointer' }}>
              Include study metadata (JSON)
            </span>
          </div>
        </div>
      </div>

      {/* Download Progress */}
      {downloading && (
        <div style={sectionGap}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14, marginBottom: 8 }}>
            <span style={{ color: COLORS.gray900 }}>Preparing download...</span>
            <span style={{ color: COLORS.teal, fontWeight: 600 }}>{downloadProgress}%</span>
          </div>
          <div style={progressTrackStyle}>
            <div style={{ ...progressFillBase, width: `${downloadProgress}%` }} />
          </div>
          <p style={{ fontSize: 12, color: COLORS.gray500, textAlign: 'center', marginTop: 8 }}>
            Compressing {totalImages} images from {studyCount} studies...
          </p>
        </div>
      )}

      {/* Footer */}
      <div style={footerStyle}>
        <Button
          onClick={handleClose}
          disabled={downloading}
          style={{
            border: `1px solid ${COLORS.gray300}`,
            background: COLORS.white,
            color: COLORS.gray700,
            height: 36,
            padding: '0 16px',
            borderRadius: 6,
            fontSize: 14,
            fontWeight: 500,
          }}
        >
          Cancel
        </Button>
        <Button
          type="primary"
          onClick={handleDownload}
          disabled={downloading}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            background: COLORS.teal,
            borderColor: COLORS.teal,
            color: COLORS.white,
            height: 36,
            padding: '0 16px',
            borderRadius: 6,
            fontSize: 14,
            fontWeight: 500,
          }}
        >
          {downloading ? (
            <>
              <Download size={16} />
              Downloading...
            </>
          ) : (
            <>
              <Download size={16} />
              Download {studyCount} {studyCount === 1 ? 'Study' : 'Studies'}
            </>
          )}
        </Button>
      </div>
    </Modal>
  );
}
